import { useCallback, useState } from "react";
import type { CheckResult, TaskCheck } from "@/core/types";

export type CheckStatus = "idle" | "running" | "done";

export interface UseTaskCheckResult {
  status: CheckStatus;
  result: CheckResult | null;
  run(): Promise<CheckResult | null>;
  clear(): void;
}

/**
 * Führt die Prüffunktion einer Aufgabe aus und fängt Fehler ab, damit ein
 * Problem beim Dokumentzugriff nicht die gesamte Oberfläche abstürzen lässt.
 * Die Meldung bleibt bewusst anwendungsneutral – derselbe Hook trägt die Kurse
 * für Word und PowerPoint.
 */
export function useTaskCheck(check: TaskCheck | undefined): UseTaskCheckResult {
  const [status, setStatus] = useState<CheckStatus>("idle");
  const [result, setResult] = useState<CheckResult | null>(null);

  const run = useCallback(async () => {
    if (!check) return null;
    setStatus("running");
    try {
      const outcome = await check();
      setResult(outcome);
      return outcome;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Beim Prüfen des Dokuments ist ein unbekannter Fehler aufgetreten.";
      const outcome: CheckResult = {
        passed: false,
        message,
        hint:
          "Stelle sicher, dass der Kurs im Seitenpanel der passenden Office-Anwendung " +
          "läuft und dort eine Datei geöffnet ist, und versuche es erneut.",
      };
      setResult(outcome);
      return outcome;
    } finally {
      setStatus("done");
    }
  }, [check]);

  const clear = useCallback(() => {
    setStatus("idle");
    setResult(null);
  }, []);

  return { status, result, run, clear };
}
